import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress, Box } from "@mui/material";
import { resetPassword, verifyUser } from "./ForgotPassword.api";
import { useNavigate } from "react-router-dom";

/* ─── Inline styles as JS objects for zero extra deps ─────────────────────── */
const S = {
  page: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#F7F6F2",
    fontFamily: "'DM Sans', sans-serif",
    height: "100vh",
  },
  card: {
    background: "#FFFFFF",
    borderRadius: "20px",
    padding: "2.5rem 2rem",
    width: "100%",
    maxWidth: "420px",
    boxShadow: "0 2px 24px rgba(0,0,0,0.07)",
    border: "1px solid #ECECEC",
  },
  iconRing: {
    width: "52px",
    height: "52px",
    borderRadius: "14px",
    background: "#F0EFF9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1.25rem",
  },
  heading: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#111",
    margin: "0 0 6px",
    letterSpacing: "-0.3px",
  },
  subtitle: {
    fontSize: "13.5px",
    color: "#888",
    lineHeight: "1.6",
    margin: "0 0 1.75rem",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "500",
    color: "#555",
    marginBottom: "6px",
  },
  inputWrap: {
    position: "relative",
    marginBottom: "4px",
  },
  input: (focused, error) => ({
    width: "100%",
    height: "44px",
    padding: "0 44px 0 14px",
    borderRadius: "10px",
    border: `1.5px solid ${error ? "#E24B4A" : focused ? "rgb(23, 96, 165)" : "#E2E2E2"}`,
    background: "#FAFAFA",
    fontSize: "14px",
    color: "#111",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color .15s",
    boxShadow: focused && !error ? "0 0 0 3px rgba(124,111,247,0.10)" : "none",
  }),
  eyeBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#AAA",
    padding: 0,
    display: "flex",
    alignItems: "center",
  },
  hintRow: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "11.5px",
    marginTop: "5px",
    marginBottom: "1rem",
    minHeight: "16px",
  },
  strengthBar: {
    display: "flex",
    gap: "4px",
    marginTop: "6px",
    marginBottom: "4px",
  },
  clickBtn : {
    color:"blue",
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  strengthSegment: (filled, level) => ({
    flex: 1,
    height: "3px",
    borderRadius: "2px",
    background: filled
      ? ["#E24B4A", "#EF9F27", "#3B6D11", "#0F6E56"][level - 1]
      : "#E8E8E8",
    transition: "background .2s",
  }),
  strengthLabel: (level) => ({
    fontSize: "11px",
    color: ["#E24B4A", "#BA7517", "#3B6D11", "#0F6E56"][level - 1] || "#AAA",
    marginBottom: "1rem",
  }),
  submitBtn: (disabled, loading) => ({
    width: "100%",
    height: "46px",
    borderRadius: "12px",
    border: "none",
    background: disabled ? "rgb(159, 197, 233)" : "rgb(23, 96, 165)",
    color: "#FFF",
    fontSize: "14.5px",
    fontWeight: "500",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginTop: "0.5rem",
    transition: "background .15s, transform .1s",
    letterSpacing: "0.1px",
  }),
  divider: {
    height: "1px",
    background: "#F0F0F0",
    margin: "1.5rem 0",
  },
  backLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    fontSize: "13px",
    color: "#888",
    cursor: "pointer",
    textDecoration: "none",
    background: "none",
    border: "none",
    width: "100%",
  },
  stateIcon: (bg) => ({
    width: "56px",
    height: "56px",
    borderRadius: "16px",
    background: bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1.25rem",
  }),
  stateTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#111",
    textAlign: "center",
    margin: "0 0 8px",
    letterSpacing: "-0.2px",
  },
  stateSub: {
    fontSize: "13.5px",
    color: "#888",
    textAlign: "center",
    lineHeight: "1.6",
  },
};

/* ─── Password strength scorer ───────────────────────────────────────────── */
function scorePassword(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const STRENGTH_LABELS = ["", "Weak", "Fair", "Good", "Strong"];

/* ─── Eye toggle icon ────────────────────────────────────────────────────── */
const EyeIcon = ({ visible }) =>
  visible ? (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

/* ─── Loading spinner dots ───────────────────────────────────────────────── */
const Dots = () => (
  <span style={{ display: "flex", gap: "5px", alignItems: "center" }}>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: "#fff",
          display: "inline-block",
          animation: "dotPulse 0.9s infinite",
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <style>{`@keyframes dotPulse{0%,80%,100%{opacity:.25;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}`}</style>
  </span>
);

/* ─── Loader s creen ──────────────────────────────────────────────────────── */
const LoaderScreen = () => (
  <Box
    display="flex"
    width="100vw"
    height="100vh"
    justifyContent="center"
    alignItems="center"
    bgcolor="#F7F6F2"
  >
    <CircularProgress size="3rem" sx={{ color: "rgb(23, 96, 165)" }} />
  </Box>
);

/* ─── Success screen ─────────────────────────────────────────────────────── */
const SuccessScreen = ({ onGoToLogin }) => {
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.stateIcon("#E8F5ED")}>
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#0F6E56"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <p style={S.stateTitle}>Password updated!</p>
        <p style={S.stateSub}>
          Your password has been changed successfully.{" "} <br />
          <a onClick={onGoToLogin} style={S.clickBtn}>
            Click here
          </a>{" "}
          to sign in with your new credentials.
        </p>
      </div>
    </div>
  );
};

/* ─── Failure screen ─────────────────────────────────────────────────────── */
const FailureScreen = () => (
  <div style={S.page}>
    <div style={S.card}>
      <div style={S.stateIcon("#FCEBEB")}>
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#A32D2D"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <p style={S.stateTitle}>Link expired</p>
      <p style={S.stateSub}>
        This password reset link is invalid or has expired. Please request a new
        reset link from the login page.
      </p>
    </div>
  </div>
);

/* ─── Main component ─────────────────────────────────────────────────────── */
const ForgotPassword = () => {
  const navigate = useNavigate(); 
  const [status, setStatus] = useState(0); // 0=loading, 1=form, 2=success, 3=error
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [cpwFocused, setCpwFocused] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const queryParameters = new URLSearchParams(window.location.search);
  const token = queryParameters.get("token");
  useEffect(() => {
    verifyUser(token)
      .then((res) => {
        if (res?.status === 200) {
          setStatus(res?.data?.success ? 1 : 3);
        } else {
          setStatus(3);
        }
      })
      .catch(() => setStatus(3));
  }, [token]);

  const strength = scorePassword(password);
  const passwordsMatch =
    password && confirmPassword && password === confirmPassword;
  const mismatch = confirmPassword && password !== confirmPassword;
  const tooShort = password && password.length < 8;
  const canSubmit = password.length >= 8 && passwordsMatch && !loading;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setErrorMsg("");
    setLoading(true);
    try {
      const res = await resetPassword(token, { password });
      if (res?.status >= 200 && res?.status <= 299) {
        setStatus(2);
      } else {
        setErrorMsg(
          res?.response?.data?.message ||
            "Something went wrong. Please try again.",
        );
      }
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [canSubmit, token, password]);

  if (status === 0) return <LoaderScreen />;
    if (status === 2) return <SuccessScreen onGoToLogin={() => navigate("/login")} />;
  if (status === 3) return <FailureScreen />;

  return (
    <div style={S.page}>
      <div style={S.card}>
        {/* Icon */}
        <div style={S.iconRing}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgb(23, 96, 165)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h1 style={S.heading}>Create new password</h1>
        <p style={S.subtitle}>
          Your new password must be at least 8 characters and different from
          your previously used passwords.
        </p>

        {/* Password field */}
        <div style={{ marginBottom: 0 }}>
          <label style={S.label} htmlFor="pw">
            New password
          </label>
          <div style={S.inputWrap}>
            <input
              id="pw"
              type={showPass ? "text" : "password"}
              style={S.input(pwFocused, tooShort && !pwFocused)}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPwFocused(true)}
              onBlur={() => setPwFocused(false)}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            <button
              style={S.eyeBtn}
              type="button"
              onClick={() => setShowPass((v) => !v)}
              tabIndex={-1}
            >
              <EyeIcon visible={showPass} />
            </button>
          </div>

          {/* Strength bar */}
          {password && (
            <>
              <div style={S.strengthBar}>
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    style={S.strengthSegment(i <= strength, strength)}
                  />
                ))}
              </div>
              <p style={S.strengthLabel(strength - 1)}>
                {STRENGTH_LABELS[strength]}
                {strength < 4 &&
                  " — add uppercase, numbers & symbols to strengthen"}
              </p>
            </>
          )}

          {tooShort && !pwFocused && (
            <div
              style={{
                ...S.hintRow,
                color: "#E24B4A",
                marginTop: password ? 0 : "5px",
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#E24B4A"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Must be at least 8 characters
            </div>
          )}
        </div>

        {/* Confirm password */}
        <div style={{ marginTop: "1rem" }}>
          <label style={S.label} htmlFor="cpw">
            Confirm password
          </label>
          <div style={S.inputWrap}>
            <input
              id="cpw"
              type={showConfirmPass ? "text" : "password"}
              style={S.input(cpwFocused, mismatch && !cpwFocused)}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={() => setCpwFocused(true)}
              onBlur={() => setCpwFocused(false)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
            <button
              style={S.eyeBtn}
              type="button"
              onClick={() => setShowConfirmPass((v) => !v)}
              tabIndex={-1}
            >
              <EyeIcon visible={showConfirmPass} />
            </button>
          </div>
          <div style={S.hintRow}>
            {passwordsMatch && (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0F6E56"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span style={{ color: "#0F6E56" }}>Passwords match</span>
              </>
            )}
            {mismatch && !cpwFocused && (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#E24B4A"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                <span style={{ color: "#E24B4A" }}>Passwords do not match</span>
              </>
            )}
          </div>
        </div>

        {/* API error */}
        {errorMsg && (
          <p
            style={{
              fontSize: "13px",
              color: "#E24B4A",
              margin: "-0.25rem 0 0.75rem",
              textAlign: "center",
            }}
          >
            {errorMsg}
          </p>
        )}

        {/* Submit */}
        <button
          style={S.submitBtn(!canSubmit, loading)}
          disabled={!canSubmit}
          onClick={handleSubmit}
          onMouseEnter={(e) => {
            if (canSubmit) e.currentTarget.style.background = "#6058E0";
          }}
          onMouseLeave={(e) => {
            if (canSubmit)
              e.currentTarget.style.background = "rgb(23, 96, 165)";
          }}
        >
          {loading ? <Dots /> : "Reset password"}
        </button>

        <div style={S.divider} />
        <button style={S.backLink} onClick={() => window.history.back()}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to sign in
        </button>
      </div>
    </div>
  );
};

export default ForgotPassword;
